import { useState } from 'react';
import { useNavigate } from 'react-router';

import { Brand } from '@/components/layout/brand';
import { SettingsMenu } from '@/components/layout/settings-menu';
import { ActionMenu, Avatar, ConfirmPanel, Drawer, Icon } from '@/components/ui';
import { ExportStatusMenu } from '@/features/exports/components/export-status-menu';
import { useLogout, useSession } from '@/features/auth/api/auth.api';
import { useUiStore } from '@/stores/ui.store';
import type { UserRole } from '@/types/domain';

const ROLE_LABELS: Record<UserRole, string> = {
  SUPER_ADMIN: 'Superuser',
  TENANT_ADMIN: 'Tenant admin',
  TENANT_CLIENT: 'Client',
  /* Never actually rendered - a guest is redirected off every route this bar
     appears on - but the map is exhaustive so the role cannot be added
     without somebody deciding what it would say. */
  JOB_GUEST: 'Customer',
};

/**
 * The full-width bar at the very top: branding on the left, appearance and
 * account on the right.
 *
 * Deliberately holds no navigation controls of its own - collapsing the
 * sidebar belongs to the sidebar, and the layout and theme switches live
 * together in the settings menu.
 */
export function Topbar() {
  const navigate = useNavigate();
  const setMobileNavOpen = useUiStore((state) => state.setMobileNavOpen);

  const { data: user } = useSession();
  const logout = useLogout();

  /* Signing out is one click away from an unsaved form, and getting back in
     means retyping a password - so it asks first, like every other action
     that throws work away. */
  const [confirmSignOut, setConfirmSignOut] = useState(false);

  function signOut(): void {
    logout.mutate(undefined, {
      /* onSettled, not onSuccess: even if the request fails the local session
         is cleared, so the user is never stranded on a page they cannot use. */
      onSettled: () => {
        setConfirmSignOut(false);
        void navigate('/login', { replace: true });
      },
    });
  }

  return (
    /* Roomier than the dense panels below it: this bar carries the branding,
       so the lockup needs space to read at a glance. */
    <header className="chrome-bar flex h-16 shrink-0 items-center justify-between gap-2 border-b px-3 md:h-14">
      <div className="flex min-w-0 items-center gap-1">
        {/* Below md both layouts collapse to the off-canvas menu. */}
        <button
          type="button"
          aria-label="Open navigation"
          className="-ml-1 p-2 text-content-muted hover:text-content md:hidden"
          onClick={() => {
            setMobileNavOpen(true);
          }}
        >
          <Icon name="menu" size={18} />
        </button>

        <Brand />
      </div>

      <div className="flex items-center gap-1">
        {/* The download status for background exports: a spinner with a count
            while sheets build, a tray of finished ones, and the save popup. */}
        <ExportStatusMenu />

        <SettingsMenu />

        <div className="mx-1 hidden h-5 w-px bg-border sm:block" aria-hidden="true" />

        {user && (
          <div className="flex items-center gap-2 pl-0.5">
            <Avatar name={user.name} />

            <div className="hidden min-w-0 leading-tight sm:block">
              <p className="truncate text-2xs font-bold text-content">{user.name}</p>
              <p className="truncate text-2xs text-content-subtle">
                {user.tenantName ?? ROLE_LABELS[user.role]}
              </p>
            </div>

            <ActionMenu
              label="Account menu"
              /* Matches the appearance button beside it - two chrome controls
                 on the same line at different sizes read as a mistake. */
              triggerClassName="sm:p-1.5"
              items={[
                {
                  label: 'Change password',
                  icon: 'key',
                  onSelect: () => {
                    void navigate('/change-password');
                  },
                },
                {
                  label: 'Sign out',
                  icon: 'logout',
                  destructive: true,
                  onSelect: () => {
                    setConfirmSignOut(true);
                  },
                },
              ]}
            />
          </div>
        )}
      </div>

      <Drawer
        open={confirmSignOut}
        onClose={() => {
          setConfirmSignOut(false);
        }}
        title="Sign out"
        description={user?.name}
        width="sm"
      >
        <ConfirmPanel
          title="Sign out of WinDocs?"
          description="Anything you have typed but not saved will be lost, and you will need your password to sign back in."
          confirmLabel="Sign out"
          loading={logout.isPending}
          onCancel={() => {
            setConfirmSignOut(false);
          }}
          onConfirm={signOut}
        />
      </Drawer>
    </header>
  );
}
